/**
 * Centralised Swiggy One journey New Relic events.
 *
 * Covers 4 touchpoints: M2C Halfcard, BullsEye TG, Item Nudge, Savings Corner Nudge.
 * Remove this file in one shot once the release is stable.
 */
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

export const SWIGGY_ONE_COMPONENT = {
    M2C_HALFCARD: "M2C_HALFCARD",
    BULLS_EYE_TG: "BULLS_EYE_TG",
    ITEM_NUDGE: "ITEM_NUDGE",
    SAVINGS_CORNER: "SAVINGS_CORNER",
} as const;

export const SWIGGY_ONE_STATE = {
    MOUNTED: "MOUNTED",
    ERROR_BOUNDARY: "ERROR_BOUNDARY",
    // data state
    DATA_VALID: "DATA_VALID",
    DATA_INVALID: "DATA_INVALID",
    // hide reasons
    HIDDEN_BY_MODAL: "HIDDEN_BY_MODAL",
    HIDDEN_FREQUENCY_CAPPED: "HIDDEN_FREQUENCY_CAPPED",
    HIDDEN_GRATIFICATION_SHOWN: "HIDDEN_GRATIFICATION_SHOWN",
    HIDDEN_OVERLAY_BLOCKED: "HIDDEN_OVERLAY_BLOCKED",
    // bulls eye tg specific
    PLAN_TYPE_INVALID: "PLAN_TYPE_INVALID",
    NO_BILL_DETAILS_CARD: "NO_BILL_DETAILS_CARD",
    // item nudge specific
    NO_COMMS: "NO_COMMS",
    MODAL_BLOCKING: "MODAL_BLOCKING",
    // savings corner specific
    NO_DATA: "NO_DATA",
} as const;

type Component = (typeof SWIGGY_ONE_COMPONENT)[keyof typeof SWIGGY_ONE_COMPONENT];
type State = (typeof SWIGGY_ONE_STATE)[keyof typeof SWIGGY_ONE_STATE];

const ERROR_BUCKET_MAP: Record<string, string> = {
    TypeError: "type_error",
    ReferenceError: "reference_error",
    SyntaxError: "syntax_error",
    RangeError: "range_error",
    ChunkLoadError: "chunk_load_error",
};

export function getErrorBucket(error?: Error): string {
    const name = error?.name || "";
    return ERROR_BUCKET_MAP[name] ?? (name ? "other_named" : "unknown");
}

export function recordSwiggyOneNREvent(component: Component, state: State, extra?: Record<string, string>): void {
    recordNREvent({
        ...extra,
        eventType: NEWRELIC_TABLES.DEBUG_SWIGGY_ONE_JOURNEY,
        component,
        state,
    });
}

// Tracks pending visibility timers keyed by target so that repeated calls
// (e.g. effects re-firing on prop updates) replace the previous timer instead
// of piling up and emitting duplicate NR events.
const pendingVisibilityTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Records a New Relic event to check if a data-testid or selector is visible for Swiggy One Widgets after a timeout
 * @param component - The component name
 * @param dataTestId - The data-testid to check
 * @param selector - The selector to check
 * @param timeout - The timeout in milliseconds
 * @returns
 */
export const recordSwiggyOneNREventDataTestIdVisibility = (
    component: Component,
    dataTestId = "",
    selector = "",
    timeout = 2500,
): void => {
    try {
        if (typeof window === "undefined") {
            return;
        }
        const timerKey = `${component}|${dataTestId}|${selector}|${timeout}`;
        const existingTimer = pendingVisibilityTimers.get(timerKey);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }
        const timerId = setTimeout(() => {
            try {
                pendingVisibilityTimers.delete(timerKey);
                let element: NodeListOf<Element> = [] as unknown as NodeListOf<Element>;
                if (!isEmpty(selector) || !isEmpty(dataTestId)) {
                    element = document.querySelectorAll(selector || `[data-testid="${dataTestId}"]`);
                }
                const visibility = element.length > 0 ? "VISIBLE" : "NOT_VISIBLE";
                recordNREvent({
                    eventType: NEWRELIC_TABLES.DEBUG_SWIGGY_ONE_JOURNEY,
                    validationType: "DATA_TESTID_VISIBILITY",
                    dataTestId,
                    selector,
                    component,
                    visibility,
                    timeout,
                });
            } catch {
                // no-op
            }
        }, timeout);
        pendingVisibilityTimers.set(timerKey, timerId);
    } catch {
        // no-op
    }
};
